import { useEffect, useRef, useCallback, useState, useMemo } from 'react'
import { createStore, useStore } from 'zustand'
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2, Search, ChevronUp, ChevronDown, X } from 'lucide-react'
import { apiService, type SheetInfo } from '@/service/api'
import SheetTabs from './SheetTabs'
import VirtualizedDataTable, { type SearchMatch, type VirtualizedDataTableHandle } from './VirtualizedDataTable'
import { ZOOM_STEP } from '@/constants/zoom'
// The '@/i18n' barrel (not bare react-i18next): importing it initialises
// i18next, and this component is rendered by suites that provide no
// I18nProvider — without the bootstrap every label renders as its key.
import { useTranslation } from '@/i18n'
import PreviewMatchList from '@/components/PreviewSearch/PreviewMatchList'
import type { PreviewMatch } from '@/components/PreviewSearch/types'

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function computeMatches(rows: unknown[][], columns: string[], query: string): SearchMatch[] {
  if (!query.trim()) return []
  const escaped = escapeRegex(query.trim())
  const regex = new RegExp(escaped, 'gi')
  const matches: SearchMatch[] = []
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    for (let colIndex = 0; colIndex < columns.length; colIndex++) {
      const value = rows[rowIndex]?.[colIndex]
      if (value === null || value === undefined) continue
      const text = String(value)
      regex.lastIndex = 0
      let occurrence = 0
      while (regex.exec(text) !== null) {
        matches.push({ rowIndex, colIndex, occurrence })
        occurrence++
      }
    }
  }
  return matches
}

// Retry on transient backend errors (502/503/504 or network failures during deploys)
const isTransientError = (err: unknown): boolean => {
  const e = err as { response?: { status?: number }; name?: string }
  const status = e.response?.status
  if (status === 502 || status === 503 || status === 504) return true
  if (e.name === 'TypeError') {
    const msg = String((e as Error).message)
    // Chrome: "Failed to fetch", Safari: "Load failed", Firefox: "NetworkError when attempting to fetch resource"
    if (msg === 'Failed to fetch' || msg === 'Load failed' || msg.startsWith('NetworkError')) return true
  }
  return false
}

// During deploys the backend is briefly unreachable. Most restarts recover in under
// 10s, some take up to 90s, and the worst case we've seen was ~3 min. These delays
// start fast then ramp up to cover all observed cases (1+2+5+10+30+60+90 = 198s).
const RETRY_DELAYS_MS = [1000, 2000, 5000, 10000, 30000, 60000, 90000]

const fetchWithRetry = async <T,>(fn: () => Promise<T>, signal?: { cancelled: boolean }): Promise<T> => {
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    try {
      return await fn()
    } catch (err) {
      if (attempt === RETRY_DELAYS_MS.length || !isTransientError(err)) throw err
      await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]))
      if (signal?.cancelled) throw err
    }
  }
  throw new Error('unreachable')
}

// ---------------------------------------------------------------------------
// FS-657: whole-sheet search. `computeMatches` only ever saw the rows on the
// current page, so a hit on row 5,000 didn't exist as far as search was
// concerned. Match discovery now happens in SQL over the entire sheet.
// ---------------------------------------------------------------------------

/** A match anywhere in the sheet, addressed by its absolute (0-based) row. */
interface SheetMatch {
  absRow: number
  colIndex: number
  occurrence: number
  /** Captured at match time: the loaded page's columns can belong to the sheet
   *  the user just switched away from. */
  columnName: string
  cellText: string
}

/** Matching rows fetched per search. Rows, not matches — a row can hold several. */
const MAX_SHEET_MATCH_ROWS = 500

/** Neutralise LIKE wildcards so a query containing % or _ matches literally. */
function escapeLike(value: string): string {
  return value.replace(/[\\%_]/g, (c) => `\\${c}`)
}

/** Backtick used as SQLite's identifier quote. Kept out of the template literals
 *  below so no source string has to contain one. */
const IDENT_QUOTE = '`'

/**
 * Quote a table/column name for SQLite.
 *
 * Backticks, not `[brackets]` and not `"double quotes"`:
 *  - brackets have no escape for a literal `]`, and datasheet headers carry them
 *    all the time (`Pressure [psig]`, `Size [in]`) — stripping the character
 *    queries a column that doesn't exist, which errors and silently degrades the
 *    whole-sheet search back to page-local;
 *  - double quotes are worse: SQLite treats a double-quoted token that doesn't
 *    resolve to a column as a *string literal*, so a wrong name would match
 *    nothing at all rather than erroring.
 * A backtick is always an identifier, and a literal one doubles.
 */
function quoteIdentifier(identifier: string): string {
  return IDENT_QUOTE + identifier.replace(/`/g, '``') + IDENT_QUOTE
}

/**
 * Rows anywhere in the sheet with a cell matching `:q`, tagged with their
 * absolute position. `rowid` is the position because ingestion writes each
 * sheet with `df.to_sql(index=False)` — a plain table, rows inserted in
 * spreadsheet order, so rowid runs 1..N and paging reads the same order.
 * LIKE is case-insensitive for ASCII in SQLite, matching the client-side regex.
 */
/**
 * The query as a number — but only when its numeric form is *textually
 * identical* to what was typed.
 *
 * That guard is what keeps the numeric comparison from inventing matches. `001`,
 * `1.0` and `1e2` all parse to numbers whose cells render as `1` or `100`, which
 * do not contain the typed text — so an equality match would highlight a cell
 * the grid's own text highlighter leaves alone, reintroducing exactly the
 * disagreement this numeric path exists to fix. Those queries fall back to
 * `LIKE`, which is the honest answer for them.
 *
 * `1.2977251093873654` round-trips identically, so the value you copied out of
 * the grid still gets the exact numeric comparison it needs.
 */
function asNumber(query: string): number | null {
  if (!/^-?\d*\.?\d+(?:[eE][+-]?\d+)?$/.test(query)) return null
  const n = Number(query)
  if (!Number.isFinite(n)) return null
  return String(n) === query ? n : null
}

/**
 * `LIKE` alone cannot find a float you copied out of the grid.
 *
 * SQLite renders a REAL as **15 significant digits** when it converts to text
 * for `LIKE`, but the grid shows the full double it received as JSON — 17
 * significant digits. So `1.2977251093873654` is stored, displayed, and pasted
 * back, yet the column reads `1.29772510938737` to `LIKE` and matches nothing.
 * (Verified against sqlite3: CAST gives the 15-digit form; the 17-digit LIKE
 * finds 0 rows; `x = <value>` finds 1.)
 *
 * So a numeric query also compares numerically, which is exact for any value
 * that round-trips through the double it was displayed from. Non-numeric
 * queries, and prefixes like `1.2977`, keep working through `LIKE`.
 */
function buildSheetSearchSql(sheet: string, columns: string[], numeric: number | null): string {
  const clauses = columns.map((col) => `${quoteIdentifier(col)} LIKE :q ESCAPE '\\'`)
  if (numeric !== null) {
    clauses.push(...columns.map((col) => `${quoteIdentifier(col)} = :qnum`))
  }
  return (
    `SELECT rowid - 1 AS __abs_row, * FROM ${quoteIdentifier(sheet)} ` +
    `WHERE ${clauses.join(' OR ')} LIMIT ${MAX_SHEET_MATCH_ROWS + 1}`
  )
}

// Zoom constants
const DEFAULT_ZOOM = 1
const MIN_ZOOM = 0.5
const MAX_ZOOM = 2
const ZOOM_PERCENT_STEP = ZOOM_STEP  // FS-524: consistent 10pp (0.1) increment

interface DatasheetPreviewProps {
  docId: string
  className?: string
  pageSize?: number
  /**
   * Emphasise and scroll to a COLUMN by name — the counterpart of a P&ID's
   * `?highlightNode=`. Not a search seed: search scans cell VALUES, and a
   * signal name is a column header, so seeding it reports "No matches" for a
   * column sitting in plain view (measured on X69PDI1109_daca_pv).
   */
  highlightColumn?: string
}

/** Which document, sheet and page a set of rows answers. */
interface RowsFor {
  docId: string
  sheet: string
  page: number
}

const SpreadsheetSkeleton = () => (
  <div className="flex items-center justify-center h-full">
    <Loader2 className="w-6 h-6 animate-spin text-gray-400 dark:text-gray-500" />
  </div>
)

// ---------------------------------------------------------------------------
// Per-instance Zustand store — each DatasheetPreview gets its own store so
// multiple instances showing different documents stay fully isolated.
// ---------------------------------------------------------------------------
interface PreviewData {
  columns: string[]
  rows: unknown[][]
}

interface SheetState {
  error: string | null
  isLoading: boolean
  isSheetsLoading: boolean
  sheets: SheetInfo[]
  activeSheet: string
  rows: unknown[][]
  columns: string[]
  /**
   * The page the current `rows` answer, or null before the first fetch lands.
   * Lets the table tell "this page is empty" from "this page hasn't arrived
   * yet" — the fast preview path never sets `isLoading`, so `rows.length === 0`
   * alone flashed "No data available" on every open — and keeps the previous
   * document's or sheet's rows off screen while the new ones load.
   */
  rowsFor: RowsFor | null
  page: number
  totalRows: number
  // Preview cache: table_name -> preview data
  previews: Record<string, PreviewData>
  resetForDoc: () => void
  setActiveSheet: (sheet: string) => void
  setSheets: (sheets: SheetInfo[], activeSheet: string) => void
  setRows: (rows: unknown[][], columns: string[], rowsFor: RowsFor) => void
  setTotalRows: (totalRows: number) => void
  setPage: (page: number) => void
  setError: (error: string) => void
  setLoading: (isLoading: boolean) => void
  setSheetsLoading: (isSheetsLoading: boolean) => void
  setPreview: (sheet: string, data: PreviewData) => void
}

const INITIAL_DATA: Omit<SheetState, keyof Omit<SheetState, 'error' | 'isLoading' | 'isSheetsLoading' | 'sheets' | 'activeSheet' | 'rows' | 'columns' | 'rowsFor' | 'page' | 'totalRows' | 'previews'>> = {
  error: null,
  isLoading: false,
  isSheetsLoading: true,
  sheets: [],
  activeSheet: '',
  rows: [],
  columns: [],
  rowsFor: null,
  page: 0,
  totalRows: 0,
  previews: {},
}

const createSheetStore = () =>
  createStore<SheetState>()((set) => ({
    ...INITIAL_DATA,
    resetForDoc: () => set({ ...INITIAL_DATA, isSheetsLoading: true }),
    setActiveSheet: (sheet) => set({ activeSheet: sheet, page: 0, totalRows: 0 }),
    setSheets: (sheets, activeSheet) => set({ sheets, activeSheet, isSheetsLoading: false }),
    setRows: (rows, columns, rowsFor) => set({ rows, columns, rowsFor }),
    setTotalRows: (totalRows) => set({ totalRows }),
    setPage: (page) => set({ page }),
    setError: (error) => set({ error }),
    setLoading: (isLoading) => set({ isLoading }),
    setSheetsLoading: (isSheetsLoading) => set({ isSheetsLoading }),
    setPreview: (sheet, data) => set((state) => ({
      previews: { ...state.previews, [sheet]: data },
    })),
  }))

const DatasheetPreview = ({
  docId,
  className,
  pageSize = 50,
  highlightColumn,
}: DatasheetPreviewProps) => {
  // Create store once per component instance
  const storeRef = useRef<ReturnType<typeof createSheetStore>>()
  if (!storeRef.current) storeRef.current = createSheetStore()
  const {
    error, isLoading, isSheetsLoading, sheets, activeSheet, rows, columns, rowsFor, page, totalRows, previews,
    resetForDoc, setActiveSheet, setSheets, setRows, setTotalRows, setPage, setError, setLoading, setSheetsLoading, setPreview,
  } = useStore(storeRef.current)

  const { t } = useTranslation('board')
  const [tableHeight, setTableHeight] = useState(400)
  const [scale, setScale] = useState(DEFAULT_ZOOM)
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const zoomContainerRef = useRef<HTMLDivElement>(null)
  const dataTableRef = useRef<VirtualizedDataTableHandle>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Search state
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0)
  const searchInputRef = useRef<HTMLInputElement>(null)
  // FS-657: matches across the whole sheet, found in SQL (see buildSheetSearchSql)
  const [sheetMatches, setSheetMatches] = useState<SheetMatch[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [matchRowsTruncated, setMatchRowsTruncated] = useState(false)

  // Read by the fallback path without re-running the search on every page turn.
  const pageDataRef = useRef({ rows, columns, page })
  pageDataRef.current = { rows, columns, page }
  const columnsKey = columns.join(' ')

  useEffect(() => {
    const query = searchQuery.trim()
    if (!query || !docId || !activeSheet || !columnsKey) {
      setSheetMatches([])
      setMatchRowsTruncated(false)
      setIsSearching(false)
      return
    }
    let cancelled = false
    setIsSearching(true)
    const timer = setTimeout(async () => {
      const { rows: pageRows, columns: cols, page: currentPage } = pageDataRef.current
      const numeric = asNumber(query)
      try {
        const result = await apiService.queryDatasheet(
          docId,
          buildSheetSearchSql(activeSheet, cols, numeric),
          numeric !== null
            ? { q: `%${escapeLike(query)}%`, qnum: numeric }
            : { q: `%${escapeLike(query)}%` }
        )
        if (cancelled) return
        const matchedRows = result?.rows ?? []
        const truncated = matchedRows.length > MAX_SHEET_MATCH_ROWS
        // The SQL prepends __abs_row, so the sheet's own columns/cells start at
        // 1. Use the response's own column list: it is the order the rows came
        // back in, which `cols` only approximates mid-sheet-switch.
        const resultCols = (result?.columns ?? []).slice(1)
        const found: SheetMatch[] = []
        for (const row of matchedRows.slice(0, MAX_SHEET_MATCH_ROWS)) {
          const absRow = Number(row[0])
          const cells = row.slice(1)
          const cellMatches = computeMatches([cells], resultCols, query)
          if (cellMatches.length > 0) {
            for (const cell of cellMatches) {
              found.push({
                absRow,
                colIndex: cell.colIndex,
                occurrence: cell.occurrence,
                columnName: resultCols[cell.colIndex] ?? '',
                cellText: String(cells[cell.colIndex] ?? ''),
              })
            }
            continue
          }
          // The float mismatch runs both ways: SQL can match a row whose text no
          // longer contains the query as the client sees it (searching SQLite's
          // 15-digit form, while the JSON cell is the 17-digit double). Locate the
          // cell numerically instead of dropping a row the database matched.
          if (numeric === null) continue
          const colIndex = cells.findIndex((c) => Number(c) === numeric)
          if (colIndex === -1) continue
          found.push({
            absRow,
            colIndex,
            occurrence: 0,
            columnName: resultCols[colIndex] ?? '',
            cellText: String(cells[colIndex] ?? ''),
          })
        }
        setSheetMatches(found)
        setMatchRowsTruncated(truncated)
      } catch {
        if (cancelled) return
        // No SQLite db for this doc (legacy), or a sheet without usable rowids.
        // Fall back to the loaded page — what search did before FS-657.
        setSheetMatches(
          computeMatches(pageRows, cols, query).map((m) => ({
            absRow: currentPage * pageSize + m.rowIndex,
            colIndex: m.colIndex,
            occurrence: m.occurrence,
            columnName: cols[m.colIndex] ?? '',
            cellText: String(pageRows[m.rowIndex]?.[m.colIndex] ?? ''),
          }))
        )
        setMatchRowsTruncated(false)
      } finally {
        if (!cancelled) setIsSearching(false)
      }
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [searchQuery, docId, activeSheet, columnsKey, pageSize])

  const activeSheetMatch = sheetMatches[currentMatchIndex] ?? null

  // Translate the active match into the table's page-relative coordinates. Null
  // while its page is still loading, so no stale cell lights up.
  const activeMatch = useMemo<SearchMatch | null>(() => {
    if (!activeSheetMatch) return null
    const rowIndex = activeSheetMatch.absRow - page * pageSize
    if (rowIndex < 0 || rowIndex >= rows.length) return null
    return { rowIndex, colIndex: activeSheetMatch.colIndex, occurrence: activeSheetMatch.occurrence }
  }, [activeSheetMatch, page, pageSize, rows.length])

  const matchListItems = useMemo<PreviewMatch[]>(
    () =>
      sheetMatches.map((m) => ({
        id: `${m.absRow}-${m.colIndex}-${m.occurrence}`,
        locationLabel: t('preview_search.row_label', { row: m.absRow + 1 }),
        snippet: m.columnName ? `${m.columnName}: ${m.cellText}` : m.cellText,
      })),
    [sheetMatches, t]
  )

  // Reset on the QUERY, not the match count: two different queries with the same
  // number of hits would otherwise leave the pointer on the last one. Also on the
  // SHEET, since switching sheets replaces the whole match set — otherwise a
  // pointer past the end of the new set reads as an impossible "5 of 2".
  useEffect(() => {
    setCurrentMatchIndex(0)
  }, [searchQuery, activeSheet])

  // Belt and braces for every other way the set can shrink under the pointer
  // (a re-query returning fewer rows, the truncation cap changing).
  useEffect(() => {
    if (currentMatchIndex >= sheetMatches.length && sheetMatches.length > 0) {
      setCurrentMatchIndex(0)
    }
  }, [sheetMatches.length, currentMatchIndex])

  // Page to the active match — but only when the ACTIVE MATCH changes, never
  // because `page` changed. Keying on `page` meant the footer pager was unusable
  // while a search was open: paging away re-fired this and snapped straight back.
  const jumpedToMatchRef = useRef<string | null>(null)
  useEffect(() => {
    if (!activeSheetMatch) {
      jumpedToMatchRef.current = null
      return
    }
    const matchKey = `${activeSheetMatch.absRow}-${activeSheetMatch.colIndex}-${activeSheetMatch.occurrence}`
    if (jumpedToMatchRef.current === matchKey) return
    jumpedToMatchRef.current = matchKey
    const targetPage = Math.floor(activeSheetMatch.absRow / pageSize)
    if (targetPage !== pageDataRef.current.page) setPage(targetPage)
  }, [activeSheetMatch, pageSize, setPage])

  // Scroll to active match
  useEffect(() => {
    if (activeMatch) {
      dataTableRef.current?.scrollToRow(activeMatch.rowIndex)
    }
  }, [activeMatch])

  const handleFindNext = useCallback(() => {
    if (sheetMatches.length === 0) return
    setCurrentMatchIndex((i) => (i + 1) % sheetMatches.length)
  }, [sheetMatches.length])

  const handleFindPrevious = useCallback(() => {
    if (sheetMatches.length === 0) return
    setCurrentMatchIndex((i) => (i - 1 + sheetMatches.length) % sheetMatches.length)
  }, [sheetMatches.length])

  const handleCloseSearch = useCallback(() => {
    setIsSearchOpen(false)
    setSearchQuery('')
    setCurrentMatchIndex(0)
    setSheetMatches([])
    setMatchRowsTruncated(false)
  }, [])

  const openSearch = useCallback(() => {
    setIsSearchOpen(true)
    setTimeout(() => {
      searchInputRef.current?.focus()
      searchInputRef.current?.select()
    }, 0)
  }, [])

  // Ctrl+F / Cmd+F keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        const target = e.target as HTMLElement
        // Don't intercept if user is typing in an unrelated input
        const isTextInput =
          target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' ||
          (target.isContentEditable && target.getAttribute('contenteditable') === 'true')
        if (isTextInput && target !== searchInputRef.current) return
        e.preventDefault()
        openSearch()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [openSearch])

  // Zoom functions
  const zoomIn = useCallback(() => {
    setScale((s) => {
      const rounded = Math.round(s / ZOOM_PERCENT_STEP) * ZOOM_PERCENT_STEP
      return Math.min(rounded + ZOOM_PERCENT_STEP, MAX_ZOOM)
    })
  }, [])

  const zoomOut = useCallback(() => {
    setScale((s) => {
      const rounded = Math.round(s / ZOOM_PERCENT_STEP) * ZOOM_PERCENT_STEP
      return Math.max(rounded - ZOOM_PERCENT_STEP, MIN_ZOOM)
    })
  }, [])

  const resetZoom = useCallback(() => {
    setScale(DEFAULT_ZOOM)
  }, [])

  // Track container height for virtualized table
  useEffect(() => {
    const container = tableContainerRef.current
    if (!container) return

    const updateHeight = () => {
      const height = container.clientHeight
      if (height > 0) {
        setTableHeight(height)
      }
    }

    updateHeight()
    // Guard: jsdom (vitest) has no ResizeObserver, so any test that renders
    // a V2 page embedding this preview crashes on mount. Browsers all have
    // it; without an observer we just keep the mount-time height.
    if (typeof ResizeObserver === 'undefined') return
    const resizeObserver = new ResizeObserver(updateHeight)
    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [])

  // Reset all state when docId changes to prevent stale data
  useEffect(() => {
    resetForDoc()
  }, [docId, resetForDoc])

  // Fetch available sheets when docId changes
  useEffect(() => {
    let cancelled = false
    const signal = { cancelled: false }

    const fetchSheets = async () => {
      if (!docId) {
        setSheetsLoading(false)
        return
      }
      try {
        const response = await fetchWithRetry(() => apiService.getDatasheetSheets(docId), signal)
        if (cancelled) return
        // Open on the sheet that HOLDS the highlighted column, when there is
        // one. A historian export can carry a tag on its second or third sheet;
        // opening on the first showed a file with no amber column anywhere —
        // the promised highlight, missing (Greptile, #34416). Falls back to the
        // first sheet when no sheet lists the column, or none was asked for.
        const holdsColumn = highlightColumn
          ? response.sheets.find((sheet) => sheet.columns?.includes(highlightColumn))
          : undefined
        const firstSheet = (holdsColumn ?? response.sheets[0])?.table_name ?? ''
        setSheets(response.sheets, firstSheet)
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load sheets')
        setSheetsLoading(false)
      }
    }
    fetchSheets()

    return () => { cancelled = true; signal.cancelled = true }
  }, [docId, highlightColumn, setSheets, setError, setSheetsLoading])

  // Fetch data when active sheet or page changes.
  // For pages within preview range, use the preview endpoint (instant, no DB load).
  // Falls back to queryDatasheet if preview is unavailable or page is beyond preview.
  useEffect(() => {
    let cancelled = false
    const signal = { cancelled: false }

    const fetchData = async () => {
      if (!docId || !activeSheet) return
      // On a docId change this effect still runs once with the previous
      // document's sheet and preview cache, after resetForDoc already cleared
      // them. Writing those rows would show the old document under the new one.
      if (storeRef.current?.getState().activeSheet !== activeSheet) return

      const offset = page * pageSize
      const end = offset + pageSize
      const key: RowsFor = { docId, sheet: activeSheet, page }

      // Check if preview data already cached and covers this page
      const cached = previews[activeSheet]
      if (cached && end <= cached.rows.length) {
        setRows(cached.rows.slice(offset, end), cached.columns, key)
        return
      }

      // For early pages, try fetching preview (fast path — no SQLite DB load)
      if (!cached && end <= 100) {
        try {
          const result = await apiService.getDatasheetPreview(docId, activeSheet)
          if (cancelled) return
          setPreview(activeSheet, { columns: result.columns, rows: result.rows })
          if (end <= result.rows.length) {
            setRows(result.rows.slice(offset, end), result.columns, key)
            return
          }
        } catch {
          // Preview unavailable (legacy doc) — fall through to queryDatasheet
        }
      }

      if (cancelled) return
      setLoading(true)
      try {
        const query = `SELECT * FROM [${activeSheet}] LIMIT ${pageSize} OFFSET ${offset}`
        const result = await fetchWithRetry(() => apiService.queryDatasheet(docId, query), signal)
        if (cancelled) return
        setRows(result?.rows ?? [], result?.columns ?? [], key)
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load data')
        setRows([], [], key)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchData()

    return () => { cancelled = true; signal.cancelled = true }
  }, [docId, activeSheet, page, pageSize, previews, setLoading, setRows, setError, setPreview])

  // Use row count from sheets metadata to avoid extra SQL queries
  useEffect(() => {
    if (!activeSheet || sheets.length === 0) return
    const sheetInfo = sheets.find((s) => s.table_name === activeSheet)
    if (sheetInfo) setTotalRows(sheetInfo.row_count)
  }, [activeSheet, sheets, setTotalRows])

  // Handle wheel events - Ctrl+Scroll for zoom, regular scroll for table navigation
  // Note: This hook must be called before any early returns to follow React's rules of hooks
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.stopPropagation() // Stop propagation to tldraw canvas (for board view)
    
    // Ctrl+Scroll to zoom
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault()
      if (e.deltaY < 0) {
        zoomIn()
      } else {
        zoomOut()
      }
    }
  }, [zoomIn, zoomOut])

  if (error && sheets.length === 0) {
    return <div className="p-4 text-red-500">{error}</div>
  }

  const hasMultipleSheets = sheets.length > 1
  const totalPages = Math.ceil(totalRows / pageSize)
  // If we have a valid row count, use it; otherwise fall back to "got full page" heuristic
  const hasNextPage = totalRows > 0
    ? page < totalPages - 1
    : rows.length === pageSize
  const hasPrevPage = page > 0
  // Never paint another document's or sheet's rows, and only call a page
  // empty once THIS page's own fetch has answered; until then it is loading.
  // Rows of the same sheet stay up while the next page loads (as before), so
  // paging swaps rows into the same table instead of remounting it, which
  // would drop the horizontal scroll and resized column widths. With no sheet
  // there is nothing to fetch, so the empty state is the answer.
  const rowsAreThisSheets = rowsFor?.docId === docId && rowsFor.sheet === activeSheet
  const awaitingRows = !!activeSheet && !(
    rowsAreThisSheets && (rows.length > 0 || rowsFor?.page === page)
  )

  return (
    <div ref={containerRef} className={`h-full flex flex-col min-h-0 ${className ?? ''}`} onWheelCapture={handleWheel} data-selectable-table>
      {/* Search bar */}
      {isSearchOpen && (
        <div
          className="flex items-center gap-2 p-2 border-b bg-white dark:bg-dark-card shrink-0"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Search className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" aria-hidden />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                if (e.shiftKey) handleFindPrevious()
                else handleFindNext()
              }
              if (e.key === 'Escape') handleCloseSearch()
            }}
            placeholder="Search in spreadsheet... (Ctrl+F)"
            className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-200 dark:border-dark-border rounded bg-white dark:bg-dark-card focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Search in spreadsheet"
          />
          {searchQuery && (
            <span className="text-xs text-gray-500 dark:text-dark-text-muted shrink-0">
              {isSearching
                ? t('preview_search.searching')
                : sheetMatches.length > 0
                  ? `${currentMatchIndex + 1} of ${sheetMatches.length}`
                  : 'No matches'}
            </span>
          )}
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={handleFindPrevious}
              onPointerDown={(e) => e.stopPropagation()}
              disabled={sheetMatches.length === 0}
              className="p-1.5 text-sm bg-gray-100 dark:bg-dark-accent border rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Previous match (Shift+Enter)"
              aria-label="Previous match"
            >
              <ChevronUp size={16} />
            </button>
            <button
              type="button"
              onClick={handleFindNext}
              onPointerDown={(e) => e.stopPropagation()}
              disabled={sheetMatches.length === 0}
              className="p-1.5 text-sm bg-gray-100 dark:bg-dark-accent border rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Next match (Enter)"
              aria-label="Next match"
            >
              <ChevronDown size={16} />
            </button>
            <button
              type="button"
              onClick={handleCloseSearch}
              onPointerDown={(e) => e.stopPropagation()}
              className="p-1.5 text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-accent rounded"
              title="Close search (Escape)"
              aria-label="Close search"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Table + the FS-657 match list, side by side */}
      <div className="flex-1 min-h-0 flex">
      {/* Data table container with constrained height */}
      <div className={`flex-1 min-w-0 min-h-0 border bg-white dark:bg-dark-card flex flex-col ${
        hasMultipleSheets ? 'rounded-t-lg rounded-b-none' : 'rounded-lg'
      }`}>
        <div ref={tableContainerRef} className="flex-1 min-h-0 overflow-hidden">
          {isSheetsLoading || awaitingRows ? (
            <SpreadsheetSkeleton />
          ) : isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400 dark:text-gray-500" />
            </div>
          ) : error ? (
            <div className="p-4 text-red-500">{error}</div>
          ) : rows.length === 0 ? (
            <div className="p-4 text-gray-500 dark:text-dark-text-muted">No data available</div>
          ) : (
            <div
              ref={zoomContainerRef}
              style={{
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
                width: `${100 / scale}%`,
                height: `${100 / scale}%`,
              }}
            >
              <VirtualizedDataTable
                ref={dataTableRef}
                columns={columns}
                rows={rows}
                height={tableHeight / scale}
                className="h-full"
                searchQuery={searchQuery.trim() || undefined}
                activeMatch={activeMatch}
                highlightColumn={highlightColumn}
              />
            </div>
          )}
        </div>

        {/* Footer: row info, zoom controls, pagination */}
        {!isSheetsLoading && !isLoading && !awaitingRows && (rows.length > 0 || page > 0) && (
          <div className="shrink-0 flex items-center px-4 py-2 border-t bg-gray-50 dark:bg-dark-muted text-sm">
            {/* Left: row count */}
            <div className="flex-1 text-gray-600 dark:text-gray-400">
              {totalRows > 0 ? (
                <>
                  Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, totalRows)} of {totalRows.toLocaleString()} rows
                </>
              ) : (
                <>Page {page + 1}</>
              )}
            </div>
            {/* Center: search + zoom controls */}
            <div className="flex-1 flex items-center justify-center gap-1">
              {/* Search was Cmd+F only, so nothing on screen said it existed.
                  It sits with the other controls rather than over the grid,
                  which is where a cell the user is reading would be. */}
              <button
                onClick={openSearch}
                onPointerDown={(e) => e.stopPropagation()}
                className={`p-1 mr-1 rounded border hover:bg-gray-100 dark:hover:bg-dark-accent ${
                  isSearchOpen
                    ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700'
                    : 'bg-white dark:bg-dark-card'
                }`}
                title="Search (Ctrl+F)"
                aria-label="Search"
              >
                <Search className="h-5 w-5" />
              </button>
              <button
                onClick={zoomOut}
                onPointerDown={(e) => e.stopPropagation()}
                disabled={scale <= MIN_ZOOM}
                className="p-1 rounded border bg-white dark:bg-dark-card hover:bg-gray-100 dark:hover:bg-dark-accent disabled:opacity-50 disabled:cursor-not-allowed"
                title="Zoom Out (Ctrl+Scroll)"
              >
                <ZoomOut className="h-5 w-5" />
              </button>
              <button
                onClick={resetZoom}
                onPointerDown={(e) => e.stopPropagation()}
                disabled={scale === DEFAULT_ZOOM}
                className="px-2 py-1 min-w-[60px] rounded border bg-white dark:bg-dark-card hover:bg-gray-100 dark:hover:bg-dark-accent disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                title="Reset Zoom"
              >
                {Math.round(scale * 100)}%
              </button>
              <button
                onClick={zoomIn}
                onPointerDown={(e) => e.stopPropagation()}
                disabled={scale >= MAX_ZOOM}
                className="p-1 rounded border bg-white dark:bg-dark-card hover:bg-gray-100 dark:hover:bg-dark-accent disabled:opacity-50 disabled:cursor-not-allowed"
                title="Zoom In (Ctrl+Scroll)"
              >
                <ZoomIn className="h-5 w-5" />
              </button>
            </div>
            {/* Right: pagination */}
            <div className="flex-1 flex items-center justify-end gap-2">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                onPointerDown={(e) => e.stopPropagation()}
                disabled={!hasPrevPage}
                className="flex items-center gap-1 px-2 py-1 rounded border bg-white dark:bg-dark-card hover:bg-gray-100 dark:hover:bg-dark-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                onPointerDown={(e) => e.stopPropagation()}
                disabled={!hasNextPage}
                className="flex items-center gap-1 px-2 py-1 rounded border bg-white dark:bg-dark-card hover:bg-gray-100 dark:hover:bg-dark-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
      {isSearchOpen && searchQuery.trim() && (
        <PreviewMatchList
          matches={matchListItems}
          activeIndex={currentMatchIndex}
          query={searchQuery}
          onSelect={setCurrentMatchIndex}
          isSearching={isSearching}
          footnote={
            matchRowsTruncated
              ? t('preview_search.first_rows_only', { rows: MAX_SHEET_MATCH_ROWS })
              : undefined
          }
          className="w-[264px] shrink-0 ml-2 border rounded-lg"
        />
      )}
      </div>

      {/* Sheet tabs at bottom like Excel - only show for multiple sheets */}
      {hasMultipleSheets && (
        <div className="shrink-0 bg-gray-100 dark:bg-dark-accent border-x border-b border-gray-300 dark:border-gray-600 rounded-b-lg px-2">
          <SheetTabs
            sheets={sheets}
            activeSheet={activeSheet}
            onSheetChange={(sheet) => setActiveSheet(sheet)}
          />
        </div>
      )}
    </div>
  )
}

export default DatasheetPreview
